import { canvasItemSchema, hashCanonicalSnapshot, makeId, spaceSchema, syncOperationSchema } from "@bedrock/domain";
import type {
  CanonicalChange,
  CanvasItem,
  HybridClock,
  RecoveredVersion,
  Snapshot,
  Space,
  SyncChange,
  SyncCheckpoint,
  SyncOperation,
  SyncOperationResult,
  SyncTransportError,
  SyncV2PullResponse,
  SyncV2PushResponse,
  SyncV2Snapshot
} from "@bedrock/domain";

export type { RecoveredVersion } from "@bedrock/domain";

export interface CachedAsset {
  assetId: string;
  blob: Blob;
  byteLength: number;
  lastAccessedAt: number;
  pinned: boolean;
}

export { normalizeSearchText, searchLocalContent, type SearchResult } from "./search";
import type { SearchResult } from "./search";

export interface SyncIssue {
  id: string;
  operation: SyncOperation;
  code: string;
  message: string;
  createdAt: number;
  entitySnapshot: Space | CanvasItem | null;
}

export function createMissingEntityRepair(issue: SyncIssue): SyncOperation | null {
  const { operation, entitySnapshot } = issue;
  if ("entity" in operation.patch || entitySnapshot === null) return null;

  const entity = operation.entityType === "item" && issue.code === "missing_item"
    ? canvasItemSchema.safeParse(entitySnapshot)
    : operation.entityType === "space" && issue.code === "missing_space"
      ? spaceSchema.safeParse(entitySnapshot)
      : null;
  if (!entity?.success || entity.data.id !== operation.entityId) return null;

  return syncOperationSchema.parse({
    ...operation,
    opId: makeId("repair"),
    baseRevision: null,
    patch: { entity: entity.data }
  });
}

export function createSnapshotRestoreOperation(entityType: "space" | "item", entity: Space | CanvasItem, deviceId: string): SyncOperation {
  const parsed = entityType === "space" ? spaceSchema.parse(entity) : canvasItemSchema.parse(entity);
  return syncOperationSchema.parse({
    opId: makeId("restore"),
    deviceId,
    entityType,
    entityId: parsed.id,
    baseRevision: null,
    clock: { wallTime: parsed.updatedAt, counter: 0, deviceId },
    patch: { entity: parsed }
  });
}

export interface LocalStore {
  initialize(): Promise<void>;
  close(): Promise<void>;
  subscribe(listener: () => void): () => void;
  listSpaces(options?: { includeDeleted?: boolean }): Promise<Space[]>;
  listItems(spaceId: string, options?: { includeDeleted?: boolean }): Promise<CanvasItem[]>;
  getItem(itemId: string): Promise<CanvasItem | undefined>;
  saveSpace(space: Space, operation: SyncOperation): Promise<void>;
  saveItem(item: CanvasItem, operation: SyncOperation): Promise<void>;
  permanentlyDelete(entityType: "space" | "item", entityId: string, operation: SyncOperation): Promise<void>;
  saveRecoveredVersion(version: RecoveredVersion): Promise<void>;
  listRecoveredVersions(itemId: string): Promise<RecoveredVersion[]>;
  importSnapshot(snapshot: Snapshot): Promise<void>;
  applyPullChanges(changes: SyncChange[]): Promise<void>;
  getOutbox(limit?: number): Promise<SyncOperation[]>;
  getPendingOperationCount(): Promise<number>;
  acknowledgeOperations(operationIds: string[]): Promise<void>;
  getCursor(): Promise<number>;
  setCursor(sequence: number): Promise<void>;
  getSyncCursor(): Promise<number | null>;
  applyCanonicalPage(changes: CanonicalChange[], nextCursor: number): Promise<void>;
  getCanonicalSnapshot(): Promise<SyncV2Snapshot>;
  stageMissingSnapshotEntities(snapshot: SyncV2Snapshot, deviceId: string): Promise<number>;
  replaceCanonicalSnapshotIfNoPending(snapshot: SyncV2Snapshot): Promise<boolean>;
  recordSyncSuccess(timestamp: number): Promise<void>;
  getLastSyncSuccess(): Promise<number | null>;
  listSyncIssues(): Promise<SyncIssue[]>;
  recoverSyncIssues(): Promise<number>;
  quarantineRejectedOperation(operation: SyncOperation, result: SyncOperationResult): Promise<void>;
  search(query: string): Promise<SearchResult[]>;
  getCachedAsset(assetId: string): Promise<CachedAsset | undefined>;
  putCachedAsset(asset: CachedAsset): Promise<void>;
  evictCachedAssets(targetBytes: number): Promise<number>;
  deleteCachedAsset(assetId: string): Promise<void>;
}

export interface SyncApi {
  bootstrap(deviceId: string): Promise<SyncV2Snapshot>;
  push(deviceId: string, cursor: number, operations: SyncOperation[]): Promise<SyncV2PushResponse>;
  pull(deviceId: string, after: number): Promise<SyncV2PullResponse>;
  checkpoint(): Promise<SyncCheckpoint>;
}

export function syncPullPath(deviceId: string, after: number, limit = 500): string {
  const query = new URLSearchParams({ deviceId, after: String(after), limit: String(limit) });
  return `/api/sync/pull?${query.toString()}`;
}

export function syncV2PullPath(deviceId: string, after: number, limit = 500): string {
  const query = new URLSearchParams({ deviceId, after: String(after), limit: String(limit) });
  return `/api/sync/v2/pull?${query.toString()}`;
}

export class SyncRequestError extends Error {
  readonly detail: SyncTransportError;

  constructor(detail: SyncTransportError) {
    super(detail.message);
    this.name = "SyncRequestError";
    this.detail = detail;
  }
}

export interface MultipartTransport {
  create(input: { assetId: string; filename: string; mimeType: string; byteLength: number }): Promise<{ assetId: string; uploadId: string; partSize: number; status: "uploading" | "ready" }>;
  uploadPart(uploadId: string, partNumber: number, body: Blob): Promise<{ partNumber: number; etag: string }>;
  complete(uploadId: string, parts: Array<{ partNumber: number; etag: string }>): Promise<void>;
}

interface PersistedMultipartUpload {
  uploadId: string;
  partSize: number;
  byteLength: number;
  parts: Array<{ partNumber: number; etag: string }>;
}

export class MultipartUploadClient {
  readonly #transport: MultipartTransport;
  readonly #storage: Storage | undefined;

  constructor(transport: MultipartTransport, storage = typeof localStorage === "undefined" ? undefined : localStorage) {
    this.#transport = transport;
    this.#storage = storage;
  }

  async upload(input: { assetId: string; filename: string; mimeType: string; blob: Blob }, onProgress?: (progress: number) => void): Promise<void> {
    const key = `bedrock-upload:${input.assetId}`;
    let state = this.#read(key);
    const created = await this.#transport.create({ assetId: input.assetId, filename: input.filename, mimeType: input.mimeType, byteLength: input.blob.size });
    if (created.status === "ready") { this.#storage?.removeItem(key); onProgress?.(1); return; }
    if (!state || state.byteLength !== input.blob.size || state.uploadId !== created.uploadId || state.partSize !== created.partSize) {
      state = { uploadId: created.uploadId, partSize: created.partSize, byteLength: input.blob.size, parts: [] };
      this.#write(key, state);
    }

    try {
      const totalParts = Math.ceil(input.blob.size / state.partSize);
      for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
        if (state.parts.some((part) => part.partNumber === partNumber)) continue;
        const start = (partNumber - 1) * state.partSize;
        const body = input.blob.slice(start, Math.min(input.blob.size, start + state.partSize), input.mimeType);
        const uploaded = await this.#retry(() => this.#transport.uploadPart(state.uploadId, partNumber, body));
        state.parts.push(uploaded);
        state.parts.sort((left, right) => left.partNumber - right.partNumber);
        this.#write(key, state);
        onProgress?.(state.parts.length / totalParts);
      }
      await this.#transport.complete(state.uploadId, state.parts);
      this.#storage?.removeItem(key);
      onProgress?.(1);
    } catch (error) {
      if (error instanceof SyncRequestError && error.detail.code === "upload_not_found") this.#storage?.removeItem(key);
      throw error;
    }
  }

  #read(key: string): PersistedMultipartUpload | undefined {
    const raw = this.#storage?.getItem(key);
    if (!raw) return undefined;
    try {
      const value = JSON.parse(raw) as PersistedMultipartUpload;
      return typeof value.uploadId === "string" && value.partSize > 0 && Array.isArray(value.parts) ? value : undefined;
    } catch { return undefined; }
  }

  #write(key: string, state: PersistedMultipartUpload): void {
    this.#storage?.setItem(key, JSON.stringify(state));
  }

  async #retry<T>(operation: () => Promise<T>): Promise<T> {
    let lastError: unknown;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      try { return await operation(); } catch (error) {
        lastError = error;
        if (error instanceof SyncRequestError && !error.detail.retryable) break;
        if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, 300 * (2 ** attempt) + Math.random() * 250));
      }
    }
    throw lastError;
  }
}

export type SyncPhase = "local-only" | "offline" | "syncing" | "up-to-date" | "retrying" | "attention";

export interface SyncStatus {
  phase: SyncPhase;
  pendingOperations: number;
  pendingAssets: number;
  pendingClips: number;
  lastSuccessfulSyncAt: number | null;
  retryAt: number | null;
  error: Pick<SyncTransportError, "code" | "message" | "correlationId"> | null;
}

export const initialSyncStatus: SyncStatus = {
  phase: "syncing",
  pendingOperations: 0,
  pendingAssets: 0,
  pendingClips: 0,
  lastSuccessfulSyncAt: null,
  retryAt: null,
  error: null
};

export class SyncEngine {
  readonly #store: LocalStore;
  readonly #api: SyncApi;
  readonly #deviceId: string;
  readonly #onState: (state: SyncStatus) => void;
  readonly #onRemoteClock: ((clock: HybridClock) => void) | undefined;
  #debounceTimer: ReturnType<typeof setTimeout> | undefined;
  #retryTimer: ReturnType<typeof setTimeout> | undefined;
  #pollTimer: ReturnType<typeof setInterval> | undefined;
  #running: Promise<void> | undefined;
  #rerunRequested = false;
  #disposed = false;
  #started = false;
  #retryAttempt = 0;
  #lastVerifiedAt = 0;
  #status: SyncStatus = { ...initialSyncStatus };

  constructor(store: LocalStore, api: SyncApi, deviceId: string, onState: (state: SyncStatus) => void, onRemoteClock?: (clock: HybridClock) => void) {
    this.#store = store;
    this.#api = api;
    this.#deviceId = deviceId;
    this.#onState = onState;
    this.#onRemoteClock = onRemoteClock;
  }

  start(): void {
    if (this.#started || this.#disposed) return;
    this.#started = true;
    this.#pollTimer = setInterval(() => void this.syncNow(), 60_000);
    void this.syncNow();
  }

  /** Syncs soon after a local change, unless nothing is waiting to be pushed. */
  schedule(): void {
    if (this.#disposed) return;
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#debounceTimer = undefined;
      void this.#syncIfPending();
    }, 300);
  }

  // A store change that queued no operation (a pulled page, a view-only update) has nothing to push,
  // and remote changes arrive through the poll, the socket and focus. Failing to count syncs anyway.
  async #syncIfPending(): Promise<void> {
    if (this.#disposed) return;
    const pending = await this.#store.getPendingOperationCount().catch(() => 1);
    if (pending > 0) await this.syncNow();
  }

  dispose(): void {
    this.#disposed = true;
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    if (this.#retryTimer) clearTimeout(this.#retryTimer);
    if (this.#pollTimer) clearInterval(this.#pollTimer);
    this.#debounceTimer = undefined;
    this.#retryTimer = undefined;
    this.#pollTimer = undefined;
  }

  async syncNow(): Promise<void> {
    if (this.#disposed) return;
    if (this.#retryTimer) {
      clearTimeout(this.#retryTimer);
      this.#retryTimer = undefined;
    }
    if (this.#running) {
      this.#rerunRequested = true;
      return this.#running;
    }
    this.#running = this.#run().finally(() => {
      this.#running = undefined;
    });
    return this.#running;
  }

  async #run(): Promise<void> {
    try {
      do {
        this.#rerunRequested = false;
        await this.#performSync();
      } while (this.#rerunRequested && !this.#disposed);
      this.#retryAttempt = 0;
    } catch (error) {
      console.error("Bedrock sync failed", error);
      await this.#handleFailure(error);
    }
  }

  async #performSync(): Promise<void> {
    await this.#store.recoverSyncIssues();
    const lastSuccessfulSyncAt = await this.#store.getLastSyncSuccess();
    this.#emit({ phase: "syncing", pendingOperations: await this.#store.getPendingOperationCount(), lastSuccessfulSyncAt, retryAt: null, error: null });

    let cursor = await this.#store.getSyncCursor();
    while (!this.#disposed) {
      const operations = await this.#store.getOutbox(500);
      if (operations.length === 0) break;
      const pushed = await this.#api.push(this.#deviceId, cursor ?? 0, operations);
      const expected = new Set(operations.map((operation) => operation.opId));
      const returned = new Set(pushed.results.map((result) => result.opId));
      if (returned.size !== pushed.results.length || operations.some((operation) => !returned.has(operation.opId)) || pushed.results.some((result) => !expected.has(result.opId))) {
        throw new SyncRequestError({ retryable: false, status: null, code: "invalid_push_response", message: "The cloud omitted an operation result", correlationId: null, retryAt: null });
      }
      const acknowledged = pushed.results.filter((result) => result.status === "applied" || result.status === "duplicate").map((result) => result.opId);
      if (acknowledged.length > 0) await this.#store.acknowledgeOperations(acknowledged);
      for (const result of pushed.results) {
        if (result.status === "rejected") {
          const operation = operations.find((candidate) => candidate.opId === result.opId);
          if (operation) await this.#store.quarantineRejectedOperation(operation, result);
        }
      }
      for (const conflict of pushed.conflicts) await this.#store.saveRecoveredVersion(conflict);
      await this.#store.recoverSyncIssues();
      this.#emit({ phase: "syncing", pendingOperations: await this.#store.getPendingOperationCount() });
    }

    const issues = await this.#store.listSyncIssues();
    if (issues.length > 0) {
      const issue = issues[0]!;
      this.#emit({
        phase: "attention",
        pendingOperations: await this.#store.getPendingOperationCount(),
        retryAt: null,
        error: { code: issue.code, message: issue.message, correlationId: null }
      });
      return;
    }

    cursor = await this.#store.getSyncCursor();
    if (cursor === null) {
      const snapshot = await this.#api.bootstrap(this.#deviceId);
      this.#observeSnapshot(snapshot);
      if (await this.#store.stageMissingSnapshotEntities(snapshot, this.#deviceId) > 0) {
        this.#rerunRequested = true;
        return;
      }
      if (!(await this.#store.replaceCanonicalSnapshotIfNoPending(snapshot))) {
        this.#rerunRequested = true;
        return;
      }
      cursor = snapshot.latestCursor;
      this.#lastVerifiedAt = Date.now();
    }

    let headCursor = cursor;
    while (!this.#disposed) {
      const pageStart = cursor;
      if (pageStart === null) throw new SyncRequestError({ retryable: false, status: null, code: "missing_sync_cursor", message: "The local sync cursor was not initialized", correlationId: null, retryAt: null });
      const pulled = await this.#api.pull(this.#deviceId, pageStart);
      if (pulled.requiresBootstrap) {
        const snapshot = await this.#api.bootstrap(this.#deviceId);
        this.#observeSnapshot(snapshot);
        if (!(await this.#store.replaceCanonicalSnapshotIfNoPending(snapshot))) {
          this.#rerunRequested = true;
          return;
        }
        cursor = snapshot.latestCursor;
        headCursor = snapshot.latestCursor;
        break;
      }
      const sequences = pulled.changes.map((change) => change.sequence);
      if (pulled.nextCursor < pageStart || pulled.nextCursor > pulled.headCursor || (pulled.hasMore && pulled.nextCursor === pageStart) || sequences.some((sequence, index) => sequence <= pageStart || sequence > pulled.nextCursor || (index > 0 && sequence <= sequences[index - 1]!))) {
        throw new SyncRequestError({ retryable: false, status: null, code: "invalid_pull_response", message: "The cloud returned an inconsistent canonical page", correlationId: null, retryAt: null });
      }
      this.#observeChanges(pulled.changes);
      await this.#store.applyCanonicalPage(pulled.changes, pulled.nextCursor);
      cursor = pulled.nextCursor;
      headCursor = pulled.headCursor;
      if (!pulled.hasMore && cursor >= headCursor) break;
    }

    if (Date.now() - this.#lastVerifiedAt >= 6 * 60 * 60 * 1000) {
      const checkpoint = await this.#api.checkpoint();
      if (cursor < checkpoint.latestCursor) {
        this.#rerunRequested = true;
        return;
      }
      const local = await this.#store.getCanonicalSnapshot();
      if (await hashCanonicalSnapshot(local) !== checkpoint.contentHash) {
        console.info(JSON.stringify({ event: "sync.v2.anti_entropy_repair", localCursor: cursor, cloudCursor: checkpoint.latestCursor }));
        const snapshot = await this.#api.bootstrap(this.#deviceId);
        this.#observeSnapshot(snapshot);
        if (!(await this.#store.replaceCanonicalSnapshotIfNoPending(snapshot))) {
          this.#rerunRequested = true;
          return;
        }
        cursor = snapshot.latestCursor;
      }
      this.#lastVerifiedAt = Date.now();
    }

    const pendingOperations = await this.#store.getPendingOperationCount();
    if (pendingOperations > 0) {
      this.#rerunRequested = true;
      return;
    }
    const successfulAt = Date.now();
    await this.#store.recordSyncSuccess(successfulAt);
    this.#emit({ phase: "up-to-date", pendingOperations: 0, lastSuccessfulSyncAt: successfulAt, retryAt: null, error: null });
  }

  async #handleFailure(error: unknown): Promise<void> {
    const detail: SyncTransportError = error instanceof SyncRequestError
      ? error.detail
      : { retryable: true, status: null, code: "network_error", message: "The cloud is temporarily unreachable", correlationId: null, retryAt: null };
    const pendingOperations = await this.#store.getPendingOperationCount().catch(() => 0);
    const offline = typeof navigator !== "undefined" && !navigator.onLine;
    if (!detail.retryable) {
      this.#emit({ phase: "attention", pendingOperations, retryAt: null, error: { code: detail.code, message: detail.message, correlationId: detail.correlationId } });
      return;
    }
    const retryAt = detail.retryAt ?? Date.now() + this.#retryDelay();
    this.#emit({ phase: offline ? "offline" : "retrying", pendingOperations, retryAt, error: { code: detail.code, message: detail.message, correlationId: detail.correlationId } });
    if (this.#disposed) return;
    this.#retryTimer = setTimeout(() => {
      this.#retryTimer = undefined;
      void this.syncNow();
    }, Math.max(0, retryAt - Date.now()));
  }

  #retryDelay(): number {
    const delays = [1_000, 2_000, 5_000, 10_000, 30_000, 60_000];
    const base = delays[Math.min(this.#retryAttempt, delays.length - 1)]!;
    this.#retryAttempt += 1;
    return Math.round(base * (.8 + Math.random() * .4));
  }

  #observeSnapshot(snapshot: SyncV2Snapshot): void {
    for (const space of snapshot.spaces) for (const clock of Object.values(space.fieldClocks)) this.#onRemoteClock?.(clock);
    for (const item of snapshot.items) for (const clock of Object.values(item.fieldClocks)) this.#onRemoteClock?.(clock);
  }

  #observeChanges(changes: CanonicalChange[]): void {
    for (const change of changes) {
      if (change.action !== "upsert" || change.kind === "recovered-version") continue;
      for (const clock of Object.values(change.entity.fieldClocks)) this.#onRemoteClock?.(clock);
    }
  }

  #emit(patch: Partial<SyncStatus> & Pick<SyncStatus, "phase">): void {
    this.#status = { ...this.#status, ...patch };
    this.#onState(this.#status);
  }
}

export class SyncInvalidationClient {
  readonly #createUrl: () => Promise<string>;
  readonly #onInvalidate: (latestSequence: number) => void;
  #socket: WebSocket | undefined;
  #timer: ReturnType<typeof setTimeout> | undefined;
  #attempt = 0;
  #stopped = true;

  constructor(createUrl: () => Promise<string>, onInvalidate: (latestSequence: number) => void) {
    this.#createUrl = createUrl;
    this.#onInvalidate = onInvalidate;
  }

  start(): void {
    if (!this.#stopped) return;
    this.#stopped = false;
    void this.#connect();
  }

  stop(): void {
    this.#stopped = true;
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = undefined;
    this.#socket?.close(1000, "Bedrock suspended");
    this.#socket = undefined;
  }

  async #connect(): Promise<void> {
    if (this.#stopped || typeof WebSocket === "undefined") return;
    try {
      const socket = new WebSocket(await this.#createUrl());
      this.#socket = socket;
      socket.addEventListener("open", () => { this.#attempt = 0; });
      socket.addEventListener("message", (event) => {
        try {
          const payload = JSON.parse(String(event.data)) as { latestSequence?: unknown };
          if (typeof payload.latestSequence === "number") this.#onInvalidate(payload.latestSequence);
        } catch {
          // Invalidations are hints; malformed messages are safe to ignore.
        }
      });
      socket.addEventListener("close", () => {
        if (this.#socket === socket) this.#socket = undefined;
        this.#scheduleReconnect();
      });
      socket.addEventListener("error", () => socket.close());
    } catch {
      this.#scheduleReconnect();
    }
  }

  #scheduleReconnect(): void {
    if (this.#stopped || this.#timer) return;
    const ceiling = Math.min(30_000, 750 * (2 ** this.#attempt));
    this.#attempt = Math.min(this.#attempt + 1, 6);
    const delay = Math.round(ceiling * (.65 + Math.random() * .7));
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      void this.#connect();
    }, delay);
  }
}
